import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, del, get, post } from '../api.js';
import { StreamLevels } from '../element-levels.js';
import { t } from '../i18n.js';
import { VuMeter } from '../vu-meter.js';

/**
 * A journalist's voice-over for a story: record it in the browser while the text scrolls as a prompter, or let
 * the AI voice read the text (when the station's key is stored). Kept with the story; played back from here.
 */
interface Voiceover {
  id: string;
  kind: 'recorded' | 'ai';
  text: string;
  voice: string | null;
  mime: string;
  durationMs: number | null;
  mediaAssetId: string | null;
  createdAt: string;
  createdBy: string | null;
}
interface VoiceoverList {
  voiceovers: Voiceover[];
  canEdit: boolean;
  aiVoice: boolean;
}

const RECORD_MIME = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.readAsDataURL(blob);
  });

export function VoiceoverPanel({ storyId, mediaAssetId, text, canEdit, onChanged }: { storyId: string; mediaAssetId: string | null; text: string; canEdit: boolean; onChanged?: () => void }) {
  const [list, setList] = useState<VoiceoverList | null>(null);
  const [script, setScript] = useState(text);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [take, setTake] = useState<{ blob: Blob; url: string; mime: string; seconds: number } | null>(null);
  const [voices, setVoices] = useState<Array<{ id: string; name: string }>>([]);
  const [voice, setVoice] = useState('');
  const recorder = useRef<MediaRecorder | null>(null);
  // the microphone on a VU meter while recording, with its name: the journalist sees the right one is heard
  const [levels] = useState(() => new StreamLevels());
  const [mic, setMic] = useState('');
  useEffect(() => () => levels.close(), [levels]);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);

  const load = useCallback(async () => {
    try {
      setList(await get<VoiceoverList>(`/api/stories/${storyId}/voiceovers`));
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : String(e));
    }
  }, [storyId]);
  useEffect(() => {
    const initial = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(initial);
  }, [load]);
  useEffect(() => {
    const initial = window.setTimeout(() => setScript(text), 0);
    return () => window.clearTimeout(initial);
  }, [text]);
  useEffect(() => {
    if (!list?.aiVoice || voices.length) return;
    const h = window.setTimeout(() => {
      get<{ voices: Array<{ id: string; name: string }> }>('/api/voiceovers/voices')
        .then((r) => {
          setVoices(r.voices);
          if (r.voices[0]) setVoice((v) => v || r.voices[0]!.id);
        })
        .catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(h);
  }, [list?.aiVoice, voices.length]);
  useEffect(() => () => void (take && URL.revokeObjectURL(take.url)), [take]);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const start = async () => {
    setNotice(null);
    levels.wake();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      levels.attach(stream);
      setMic(stream.getAudioTracks()[0]?.label ?? '');
      const mime = RECORD_MIME.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        levels.close();
        const type = (rec.mimeType || mime || 'audio/webm').split(';')[0]!;
        const blob = new Blob(chunks.current, { type });
        setTake({ blob, url: URL.createObjectURL(blob), mime: type, seconds: Math.round((Date.now() - startedAt.current) / 100) / 10 });
        setRecording(false);
      };
      recorder.current = rec;
      startedAt.current = Date.now();
      rec.start(250);
      setRecording(true);
    } catch (e) {
      levels.close();
      setNotice(e instanceof Error && e.name === 'NotAllowedError' ? t('voMicDenied') : e instanceof Error ? e.message : String(e));
    }
  };
  const stop = () => recorder.current?.state !== 'inactive' && recorder.current?.stop();
  const save = () =>
    run('save', async () => {
      if (!take) return;
      await post(`/api/stories/${storyId}/voiceovers`, { audio: await toBase64(take.blob), mime: take.mime, text: script, mediaAssetId, durationMs: Math.round(take.seconds * 1000) });
      setTake(null);
      await load();
      onChanged?.();
      setNotice(t('voSaved'));
    });
  const aiRead = () =>
    run('ai', async () => {
      if (!script.trim() || !voice) return;
      await post(`/api/stories/${storyId}/voiceovers/ai`, { text: script.trim(), voice, mediaAssetId });
      await load();
      onChanged?.();
      setNotice(t('voAiDone'));
    });
  const remove = (id: string) =>
    run('remove', async () => {
      await del(`/api/stories/${storyId}/voiceovers/${id}`);
      await load();
      onChanged?.();
    });

  const editable = canEdit && (list?.canEdit ?? false);
  return (
    <div className="vo">
      <ul className="vo-list">
        {(list?.voiceovers ?? []).map((v) => (
          <li key={v.id}>
            <span className={`vo-kind ${v.kind}`}>{v.kind === 'ai' ? '✦' : '🎙'}</span>
            <span className="vo-meta">
              <b>{v.kind === 'ai' ? `${t('voAi')}${v.voice ? ` · ${v.voice}` : ''}` : (v.createdBy ?? t('voRecorded'))}</b>
              <span className="muted small">
                {' '}
                {new Date(v.createdAt).toLocaleString('el-GR', { timeZone: 'Europe/Nicosia', hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                {v.durationMs ? ` · ${Math.round(v.durationMs / 1000)}″` : ''}
              </span>
            </span>
            <audio controls preload="none" src={`/api/stories/${storyId}/voiceovers/${v.id}/audio`} />
            <button type="button" className="btn small danger" disabled={!editable || !!busy} onClick={() => remove(v.id)} title={t('delete')}>
              ✖
            </button>
          </li>
        ))}
      </ul>
      {list && list.voiceovers.length === 0 && <p className="muted small">{t('voNone')}</p>}
      <textarea className={`vo-script${recording ? ' live' : ''}`} rows={4} value={script} disabled={!editable || recording} placeholder={t('voScriptPh')} onChange={(e) => setScript(e.target.value)} />
      <div className="vo-row">
        {!recording ? (
          <button type="button" className="btn small primary" disabled={!editable || !!busy} onClick={() => void start()} title={t('voRecordHint')}>
            ● {t('voRecord')}
          </button>
        ) : (
          <button type="button" className="btn small danger" onClick={stop}>
            ■ {t('voStop')}
          </button>
        )}
        {recording && (
          <div className="vo-meter">
            <VuMeter levels={levels} horizontal />
            {mic && <span className="muted small">🎙 {mic}</span>}
          </div>
        )}
        {take && !recording && (
          <>
            <audio controls src={take.url} />
            <span className="muted small">{take.seconds}″</span>
            <button type="button" className="btn small primary" disabled={!!busy} onClick={save}>
              {busy === 'save' ? '…' : t('voSave')}
            </button>
            <button type="button" className="btn small" disabled={!!busy} onClick={() => setTake(null)}>
              {t('voDiscard')}
            </button>
          </>
        )}
      </div>
      <div className="vo-row">
        {list?.aiVoice ? (
          <>
            <select value={voice} disabled={!editable || !!busy} onChange={(e) => setVoice(e.target.value)}>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn small" disabled={!editable || !!busy || !voice || !script.trim()} onClick={aiRead} title={t('voAiHint')}>
              {busy === 'ai' ? '…' : `✦ ${t('voAiRead')}`}
            </button>
          </>
        ) : (
          list && <span className="muted small">{t('voAiOff')}</span>
        )}
      </div>
      <p className="muted small">{t('voAirNote')}</p>
      {notice && <div className="banner info">{notice}</div>}
    </div>
  );
}
